import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { alertActions } from "_store";
import { Typography, Button, Box } from "@mui/material";
import Popper from "@mui/material/Popper";
import Grid from "@material-ui/core/Grid";
import { ERR_MSG } from "constants/labels";
import { PIPELINE_FIELD_MAPPING, createPipelineSchema } from "../../utils";
import PipelineFormFields from "../PipelineFormFields";

const fm = PIPELINE_FIELD_MAPPING;

const toFormValues = (pipelineData) => ({
  [fm.name]: pipelineData?.[fm.name] || "",
  [fm.description]: pipelineData?.[fm.description] || "",
  [fm.isActive]:
    pipelineData?.[fm.isActive] !== undefined ? !!pipelineData[fm.isActive] : true,
  [fm.isConsumptionVisible]:
    pipelineData?.[fm.isConsumptionVisible] !== undefined
      ? !!pipelineData[fm.isConsumptionVisible]
      : true,
  [fm.isStorageVisible]:
    pipelineData?.[fm.isStorageVisible] !== undefined
      ? !!pipelineData[fm.isStorageVisible]
      : true,
});

const PipelineEdit = ({
  pipelineData,
  pipelines,
  anchorEl,
  isOpen,
  onClose,
  handleRefresh,
  updateApi,
  header = "Manage Marketer Pipelines",
  title = "Edit Pipeline",
}) => {
  const dispatch = useDispatch();
  const [isDisable, setIsDisable] = useState(false);

  const canBeOpen = isOpen && Boolean(anchorEl);
  const id = canBeOpen ? "edit-pipeline-popper" : undefined;

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
    watch,
    trigger,
  } = useForm({
    resolver: yupResolver(createPipelineSchema()),
    mode: "onBlur",
    defaultValues: toFormValues(pipelineData),
  });

  const watchedFields = watch();
  const prevFormValues = useRef(watchedFields);

  useEffect(() => {
    if (pipelineData) {
      reset(toFormValues(pipelineData));
    }
  }, [pipelineData, reset]);

  useEffect(() => {
    const hasChanged = Object.keys(watchedFields).some((key) => {
      return watchedFields[key] !== prevFormValues.current[key];
    });

    if (hasChanged && isDisable) {
      setIsDisable(false);
    }

    prevFormValues.current = watchedFields;
  }, [watchedFields, isDisable]);

  const onSubmit = async (data) => {
    setIsDisable(true);
    try {
      dispatch(alertActions.clear());

      // Check for duplicate names (excluding current item)
      const isDuplicate = (pipelines || []).some(
        (item) =>
          item[fm.name]?.toLowerCase() === data[fm.name].trim().toLowerCase() &&
          item[fm.id] !== pipelineData[fm.id]
      );

      if (isDuplicate) {
        dispatch(
          alertActions.error({
            message: `${fm.nameLabel} already exists.`,
            header,
          })
        );
        setIsDisable(false);
        return;
      }

      const transformedData = {
        Data: {
          [fm.id]: pipelineData[fm.id] || 0,
          [fm.name]: data[fm.name].trim(),
          [fm.description]: data[fm.description].trim(),
          [fm.isActive]: !!data[fm.isActive],
          [fm.isConsumptionVisible]: !!data[fm.isConsumptionVisible],
          [fm.isStorageVisible]: !!data[fm.isStorageVisible],
        },
      };

      const result = await dispatch(updateApi(transformedData)).unwrap();

      if (!result || !result?.Succeeded) {
        dispatch(
          alertActions.error({
            message: result?.Message || ERR_MSG["073"],
            header,
          })
        );
        setIsDisable(false);
        return;
      }

      dispatch(
        alertActions.success({
          message: result?.Message || "Updated successfully",
          header,
        })
      );

      setTimeout(() => {
        handleRefresh();
      }, 1000);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header,
        })
      );
      setIsDisable(false);
    } finally {
      onClose();
    }
  };

  const handleCancelClick = () => {
    reset(toFormValues(pipelineData));
    onClose();
  };

  return (
    <Popper
      id={id}
      open={canBeOpen}
      anchorEl={anchorEl}
      className="Filtercontainer"
      placement="left-start"
    >
      <Box
        sx={{ border: 1, p: 1, bgcolor: "background.paper" }}
        className="Filtercontainerinner CreateMarketerhight Border"
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="Registrationcontainer marketerFiltercontainer"
        >
          <Typography component="div" className="userprofilelist">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h2" className="userprofilelistcontent">
                  {title}
                </Typography>
              </Grid>
            </Grid>
          </Typography>

          <PipelineFormFields
            control={control}
            errors={errors}
            trigger={trigger}
          />

          <Typography component="div" className="CreateMarketerbutton">
            <Button
              type="submit"
              variant="contained"
              className="submitbutton"
              color="primary"
              disabled={!isValid || isDisable}
            >
              Update
            </Button>
            <Button
              variant="contained"
              color="red"
              className="cancelbutton"
              onClick={handleCancelClick}
            >
              Cancel
            </Button>
          </Typography>
        </form>
      </Box>
    </Popper>
  );
};

export default PipelineEdit;
